import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { KeyRound, ShieldAlert, Fingerprint, MonitorSmartphone, X } from "lucide-react";
import { startRegistration } from '@simplewebauthn/browser';
import storage from "utils/Storage";
import Cookies from "js-cookie";
import { sendNotfication, API } from 'utils/Helper';
import { QRCode } from "react-qrcode-logo";


const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background-color: #292d2e;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #3a4142;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  margin: 0;
`;

const Description = styled.p`
  color: #9ca3af;
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #3a4142;
  margin: 20px 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: bold;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  background-color: #1e2024;
  border: 1px solid #3a4142;
  border-radius: 6px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  
  &:focus {
    border-color: #1fc16b;
  }
`;

const ActionButton = styled.button`
  height: 48px;
  padding: 0 24px;
  border-radius: 6px;
  background-color: #1fc16b;
  color: #000;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 12px;
  border: none;
  transition: all 0.2s;
  cursor: pointer;
  width: fit-content;

  &:hover {
    background-color: #1ab060;
  }
  
  ${props => props.secondary && `
    background-color: #3a4142;
    color: #fff;
    &:hover {
      background-color: #4a5152;
    }
  `}

  ${props => props.danger && `
    background-color: #ef4444;
    color: #fff;
    &:hover {
      background-color: #dc2626;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
`;

const SessionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #1e2024;
  border-radius: 6px;
  border: 1px solid #3a4142;
`;

const SessionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SessionName = styled.span`
  color: #fff;
  font-weight: bold;
  font-size: 14px;
`;

const SessionDetails = styled.span`
  color: #9ca3af;
  font-size: 12px;
`;

const PasskeyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding: 0 4px;
`;

const PageInfo = styled.span`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;
`;

const PageButton = styled.button`
  background-color: #3a4142;
  border: none;
  border-radius: 4px;
  color: #fff;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #4a5152;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PasskeyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #1e2024;
  border-radius: 6px;
  border: 1px solid #3a4142;
`;

const PasskeyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PasskeyName = styled.span`
  color: #fff;
  font-weight: bold;
  font-size: 14px;
`;

const PasskeyDetails = styled.span`
  color: #9ca3af;
  font-size: 12px;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1e2024;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  border: 1px solid #3a4142;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  
  &:hover {
    color: #fff;
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  background-color: #fff;
  padding: 16px;
  border-radius: 8px;
`;

const Security = () => {
  const getUid = () => {
    let uid = Cookies.get('uid');
    if (!uid) {
      try {
        const token = storage.getKey('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          uid = payload.id || payload.uid;
        }
      } catch (e) {
        console.error("Could not parse token", e);
      }
    }
    return uid;
  };

  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordConfig, setPasswordConfig] = useState(null);
  
  // 2FA State
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [show2faModal, setShow2faModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [twoFaSecret, setTwoFaSecret] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaForLogin, setTwoFaForLogin] = useState(true);
  const [twoFaForWithdraw, setTwoFaForWithdraw] = useState(false);

  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [passkeys, setPasskeys] = useState([]);

  // Pagination State
  const [sessionsPage, setSessionsPage] = useState(1);
  const [passkeysPage, setPasskeysPage] = useState(1);
  const ITEMS_PER_PAGE = 3;

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetch2FaStatus();
    fetchSessions();
    fetchPasskeys();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API}/api/auth/requirements?type=reset`);
      const json = await res.json();
      if (json.status && json.data?.fields_config?.password) {
        setPasswordConfig(json.data.fields_config.password);
      }
    } catch (err) {
      console.error("Failed to fetch password requirements", err);
    }
  };

  const fetch2FaStatus = async () => {
    try {
      const uid = getUid();
      if (!uid) return;
      const res = await fetch(`${API}/2fa/status/${uid}`);
      const data = await res.json();
      if (data.success) {
        setIs2faEnabled(data.is2faEnabled);
        setTwoFaForLogin(data.twoFaForLogin ?? true);
        setTwoFaForWithdraw(data.twoFaForWithdraw ?? false);
      }
    } catch (err) {
      console.error("Failed to fetch 2FA status", err);
    }
  };

  const handleToggle2FASetting = async (forLoginVal, forWithdrawVal) => {
    if (!twoFaCode || twoFaCode.length !== 6) {
      return sendNotfication("Please enter the 6-digit Authenticator Code to change settings", "error");
    }
    try {
      const uid = getUid();
      if (!uid) return;
      setTwoFaLoading(true);
      const res = await fetch(`${API}/2fa/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid,
          twoFaForLogin: forLoginVal,
          twoFaForWithdraw: forWithdrawVal,
          token: twoFaCode
        })
      });
      const data = await res.json();
      if (data.success) {
        sendNotfication("2FA preferences updated", "success");
        setTwoFaForLogin(forLoginVal);
        setTwoFaForWithdraw(forWithdrawVal);
        setTwoFaCode('');
      } else {
        sendNotfication(data.error || "Failed to update settings", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const uid = getUid();
      if (!uid) return;
      const res = await fetch(`${API}/sessions/${uid}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.sessions)) {
        setSessions(data.sessions);
      }
    } catch (err) {
      console.error("Failed to fetch sessions", err);
    }
  };

  const fetchPasskeys = async () => {
    try {
      const uid = getUid();
      if (!uid) return;
      const res = await fetch(`${API}/passkeys/list/${uid}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.passkeys)) {
        setPasskeys(data.passkeys);
      }
    } catch (err) {
      console.error("Failed to fetch passkeys", err);
    }
  };

  const handleDeletePasskey = async (passkeyId) => {
    if (!window.confirm("Are you sure you want to delete this passkey?")) return;
    setPasskeyLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/passkeys/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: passkeyId, uid })
      });
      const data = await res.json();
      if (data.success) {
        sendNotfication("Passkey deleted successfully", "success");
        fetchPasskeys();
      } else {
        sendNotfication(data.error || "Failed to delete passkey", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return sendNotfication("Please fill in all password fields", "error");
    }
    if (newPassword !== confirmPassword) {
      return sendNotfication("New passwords do not match", "error");
    }

    if (passwordConfig) {
      if (newPassword.length < (passwordConfig.minLength || 6)) {
        return sendNotfication(`Password must be at least ${passwordConfig.minLength} characters`, "error");
      }
      if (passwordConfig.requireUppercase && !/[A-Z]/.test(newPassword)) {
        return sendNotfication("Password must contain an uppercase letter", "error");
      }
      if (passwordConfig.requireNumbers && !/[0-9]/.test(newPassword)) {
        return sendNotfication("Password must contain a number", "error");
      }
      if (passwordConfig.requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
        return sendNotfication("Password must contain a special character", "error");
      }
    }

    setPasswordLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storage.getKey('token')}`
        },
        body: JSON.stringify({ uid, currentPassword, newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        sendNotfication(data.message || "Password changed successfully", "success");
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        sendNotfication(data.error || "Failed to change password", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleGenerate2FA = async () => {
    setTwoFaLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/2fa/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid })
      });
      const data = await res.json();
      if (data.success) {
        setQrCodeUrl(data.data.qrCodeUrl);
        setTwoFaSecret(data.data.secret);
        setShow2faModal(true);
      } else {
        sendNotfication(data.error || "Failed to generate 2FA", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFaCode || twoFaCode.length !== 6) {
      return sendNotfication("Please enter a valid 6-digit code", "error");
    }

    setTwoFaLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token: twoFaCode })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        sendNotfication("2FA enabled successfully", "success");
        setIs2faEnabled(true);
        setShow2faModal(false);
        setTwoFaCode('');
      } else {
        sendNotfication(data.error || data.message || "Invalid 2FA code", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFaCode || twoFaCode.length !== 6) {
      return sendNotfication("Please enter a valid 6-digit code to disable 2FA", "error");
    }

    setTwoFaLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token: twoFaCode })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        sendNotfication("2FA disabled successfully", "success");
        setIs2faEnabled(false);
        setTwoFaCode('');
      } else {
        sendNotfication(data.error || data.message || "Invalid 2FA code", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setSessionLoading(true);
    try {
      const uid = getUid();
      const res = await fetch(`${API}/sessions/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, sessionId })
      });
      const data = await res.json();
      if (data.success) {
        sendNotfication(data.message || "Session revoked", "success");
        fetchSessions();
      } else {
        sendNotfication(data.error || "Failed to revoke session", "error");
      }
    } catch (err) {
      sendNotfication("Network error", "error");
    } finally {
      setSessionLoading(false);
    }
  };

  const handleCreatePasskey = async () => {
    setPasskeyLoading(true);
    try {
      const uid = getUid();
      const username = storage.getKey('name') || 'user';
      
      const optionsRes = await fetch(`${API}/passkeys/generate-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, username })
      });
      const optionsData = await optionsRes.json();
      
      if (!optionsData.success) {
        throw new Error(optionsData.error || 'Failed to generate options');
      }
      
      let attResp;
      try {
        attResp = await startRegistration(optionsData.options);
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          throw new Error('Registration was cancelled or not allowed');
        }
        throw error;
      }
      
      const verificationRes = await fetch(`${API}/passkeys/verify-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, response: attResp })
      });
      const verificationData = await verificationRes.json();
      
      if (verificationData.success) {
        sendNotfication('Passkey successfully registered!', 'success');
        fetchPasskeys();
      } else {
        throw new Error(verificationData.error || 'Failed to verify passkey');
      }
    } catch (err) {
      console.error(err);
      sendNotfication(err.message || 'Error creating passkey', 'error');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const paginatedPasskeys = passkeys.slice((passkeysPage - 1) * ITEMS_PER_PAGE, passkeysPage * ITEMS_PER_PAGE);
  const totalPasskeysPages = Math.ceil(passkeys.length / ITEMS_PER_PAGE);

  const paginatedSessions = sessions.slice((sessionsPage - 1) * ITEMS_PER_PAGE, sessionsPage * ITEMS_PER_PAGE);
  const totalSessionsPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);

  return (
    <PageContainer>
      {/* Change Password */}
      <Card>
        <CardHeader>
          <KeyRound size={24} color="#1fc16b" />
          <Title>Change Password</Title>
        </CardHeader>
        <Description>
          Ensure your account is using a long, random password to stay secure.
          {passwordConfig && (
            <span style={{display: 'block', marginTop: '8px', fontSize: '12px', color: '#1fc16b'}}>
              Requirements: Min {passwordConfig.minLength || 6} chars
              {passwordConfig.requireUppercase && ', 1 uppercase'}
              {passwordConfig.requireNumbers && ', 1 number'}
              {passwordConfig.requireSpecialChar && ', 1 special character'}
            </span>
          )}
        </Description>
        <Divider />
        
        <FormGroup>
          <Label>Current Password</Label>
          <Input 
            type="password" 
            placeholder="Enter current password" 
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </FormGroup>
        
        <FormGroup>
          <Label>New Password</Label>
          <Input 
            type="password" 
            placeholder="Enter new password" 
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Confirm New Password</Label>
          <Input 
            type="password" 
            placeholder="Confirm new password" 
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </FormGroup>
        
        <ActionButton className="mt-2" onClick={handleChangePassword} disabled={passwordLoading}>
          {passwordLoading ? 'Updating...' : 'Update Password'}
        </ActionButton>
      </Card>

      {/* Two Factor Auth */}
      <Card>
        <CardHeader>
          <ShieldAlert size={24} color="#3b82f6" />
          <Title>Two-Factor Authentication (2FA)</Title>
        </CardHeader>
        <Description>
          Add additional security to your account using two factor authentication.
        </Description>
        <Divider />
        
        {!is2faEnabled ? (
          <ActionButton onClick={handleGenerate2FA} disabled={twoFaLoading}>Enable 2FA</ActionButton>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '300px' }}>
            <span style={{ color: '#1fc16b', fontWeight: 'bold' }}>✓ 2FA is currently enabled</span>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff', fontSize: '13px' }}>
                <input 
                  type="checkbox" 
                  checked={twoFaForLogin} 
                  disabled={twoFaLoading}
                  onChange={(e) => handleToggle2FASetting(e.target.checked, twoFaForWithdraw)} 
                  style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }}
                />
                Require 2FA for Login
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff', fontSize: '13px' }}>
                <input 
                  type="checkbox" 
                  checked={twoFaForWithdraw} 
                  disabled={twoFaLoading}
                  onChange={(e) => handleToggle2FASetting(twoFaForLogin, e.target.checked)} 
                  style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }}
                />
                Require 2FA for Withdrawal
              </label>
            </div>

            <FormGroup style={{ marginBottom: 0 }}>
              <Label>Authenticator Code</Label>
              <Input 
                type="text" 
                placeholder="000000" 
                maxLength={6}
                value={twoFaCode}
                onChange={(e) => setTwoFaCode(e.target.value)}
              />
            </FormGroup>
            <ActionButton danger onClick={handleDisable2FA} disabled={twoFaLoading}>Disable 2FA</ActionButton>
          </div>
        )}
      </Card>

      {/* Passkeys */}
      <Card>
        <CardHeader>
          <Fingerprint size={24} color="#a855f7" />
          <Title>Passkeys</Title>
        </CardHeader>
        <Description>
          With passkeys you don't need to remember complex passwords. Passkeys provide a secure way to login using your device's biometric authentication.
        </Description>
        <Divider />
        <ActionButton onClick={handleCreatePasskey} disabled={passkeyLoading}>
          {passkeyLoading ? 'Processing...' : 'Create Passkey'}
        </ActionButton>

        {passkeys.length > 0 && (
          <>
            <PasskeyList>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>Registered Passkeys</div>
              {paginatedPasskeys.map((passkey, index) => {
                const globalIndex = (passkeysPage - 1) * ITEMS_PER_PAGE + index;
                return (
                  <PasskeyItem key={passkey.id}>
                    <PasskeyInfo>
                      <PasskeyName>Passkey #{globalIndex + 1}</PasskeyName>
                      <PasskeyDetails>
                        Transports: {Array.isArray(passkey.transports) ? passkey.transports.join(', ') : 'unknown'} • 
                        Used Count: {passkey.counter}
                      </PasskeyDetails>
                    </PasskeyInfo>
                    <ActionButton 
                      danger 
                      onClick={() => handleDeletePasskey(passkey.id)}
                      disabled={passkeyLoading}
                      style={{ padding: '0 16px', height: '36px' }}
                    >
                      Delete
                    </ActionButton>
                  </PasskeyItem>
                );
              })}
            </PasskeyList>
            {totalPasskeysPages > 1 && (
              <PaginationContainer>
                <PageButton 
                  disabled={passkeysPage === 1} 
                  onClick={() => setPasskeysPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </PageButton>
                <PageInfo>Page {passkeysPage} of {totalPasskeysPages}</PageInfo>
                <PageButton 
                  disabled={passkeysPage === totalPasskeysPages} 
                  onClick={() => setPasskeysPage(prev => Math.min(prev + 1, totalPasskeysPages))}
                >
                  Next
                </PageButton>
              </PaginationContainer>
            )}
          </>
        )}
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <MonitorSmartphone size={24} color="#f59e0b" />
          <Title>Active Sessions</Title>
        </CardHeader>
        <Description>
          Manage and log out your active sessions on other browsers and devices.
        </Description>
        <Divider />
        
        <SessionList>
          {paginatedSessions.map(session => (
            <SessionItem key={session.id}>
              <SessionInfo>
                <SessionName>{session.user_agent || 'Unknown Device'}</SessionName>
                <SessionDetails>
                  IP: {session.ip_address} • 
                  Created: {new Date(session.created_at).toLocaleDateString()}
                </SessionDetails>
              </SessionInfo>
              <ActionButton 
                danger 
                onClick={() => handleRevokeSession(session.id)}
                disabled={sessionLoading}
                style={{ padding: '0 16px', height: '36px' }}
              >
                Revoke
              </ActionButton>
            </SessionItem>
          ))}
          {sessions.length === 0 && (
            <SessionDetails style={{ textAlign: 'center', padding: '16px 0' }}>
              No active sessions found.
            </SessionDetails>
          )}
        </SessionList>

        {totalSessionsPages > 1 && (
          <PaginationContainer>
            <PageButton 
              disabled={sessionsPage === 1} 
              onClick={() => setSessionsPage(prev => Math.max(prev - 1, 1))}
            >
              Previous
            </PageButton>
            <PageInfo>Page {sessionsPage} of {totalSessionsPages}</PageInfo>
            <PageButton 
              disabled={sessionsPage === totalSessionsPages} 
              onClick={() => setSessionsPage(prev => Math.min(prev + 1, totalSessionsPages))}
            >
              Next
            </PageButton>
          </PaginationContainer>
        )}
        
        {sessions.length > 0 && (
          <ActionButton 
            secondary 
            className="mt-4" 
            onClick={() => handleRevokeSession('all')}
            disabled={sessionLoading}
          >
            Log Out All Other Sessions
          </ActionButton>
        )}
      </Card>

      {/* 2FA Setup Modal */}
      {show2faModal && (
        <ModalOverlay>
          <ModalContent style={{ maxWidth: '300px', padding: '16px', borderRadius: '10px' }}>
            <CloseButton onClick={() => setShow2faModal(false)}>
              <X size={16} />
            </CloseButton>
            <Title style={{ marginBottom: '8px', fontSize: '15px', fontWeight: 'bold', textAlign: 'center' }}>Set up 2FA</Title>
            <Description style={{ textAlign: 'center', marginBottom: '8px', fontSize: '11px', lineHeight: '1.3' }}>
              Scan this QR code with your authenticator app (like Google Authenticator or Authy).
            </Description>
            
            {qrCodeUrl && (
              <QRCodeContainer style={{ background: '#fff', padding: '6px', borderRadius: '6px', margin: '8px auto', width: 'fit-content', display: 'flex', justifyContent: 'center' }}>
                <QRCode value={qrCodeUrl} size={160} />
              </QRCodeContainer>
            )}
            
            <Description style={{ textAlign: 'center', wordBreak: 'break-all', fontSize: '11px', margin: '8px 0' }}>
              Or enter this code manually:<br />
              <span style={{ 
                display: 'inline-block',
                marginTop: '4px',
                padding: '4px 10px',
                backgroundColor: '#16181c', 
                border: '1px solid #3a4142', 
                borderRadius: '4px', 
                color: '#fff', 
                fontSize: '12px', 
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {twoFaSecret}
              </span>
            </Description>

            <FormGroup style={{ marginTop: '12px', marginBottom: 0 }}>
              <Label style={{ fontSize: '9px', marginBottom: '4px', textAlign: 'center', display: 'block' }}>Enter the 6-digit code from your app</Label>
              <Input 
                type="text" 
                placeholder="000000" 
                maxLength={6}
                value={twoFaCode}
                onChange={(e) => setTwoFaCode(e.target.value)}
                style={{ textAlign: 'center', fontSize: '18px', padding: '6px 10px', letterSpacing: '4px', height: '36px' }}
              />
            </FormGroup>

            <ActionButton 
              style={{ width: '100%', marginTop: '12px', height: '36px', fontSize: '10px' }} 
              onClick={handleVerify2FA}
              disabled={twoFaLoading}
            >
              {twoFaLoading ? 'Verifying...' : 'Verify and Enable'}
            </ActionButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Security;
